/**
 * REUSABLE CONTEXT - AuthContext
 * Authentication context provider
 * Can be adapted for any React project
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { storage } from '../utils/helpers';

const AuthContext = createContext(null);

export const AuthProvider = ({
  children,
  tokenKey = 'auth_token',
  userKey = 'auth_user',
  onLoginSuccess,
  onLogoutSuccess,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check authentication on mount
  useEffect(() => {
    const storedToken = storage.get(tokenKey);
    const storedUser = storage.get(userKey);

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(storedUser);
      setIsAuthenticated(true);
    }

    setLoading(false);
  }, [tokenKey, userKey]);

  // Login
  const login = (authToken, userData) => {
    storage.set(tokenKey, authToken);
    storage.set(userKey, userData);

    setToken(authToken);
    setUser(userData);
    setIsAuthenticated(true);

    if (onLoginSuccess) {
      onLoginSuccess(userData);
    }
  };

  // Logout
  const logout = () => {
    storage.remove(tokenKey);
    storage.remove(userKey);

    setToken(null);
    setUser(null);
    setIsAuthenticated(false);

    if (onLogoutSuccess) {
      onLogoutSuccess();
    }
  };

  // Update user
  const updateUser = (updatedUser) => {
    storage.set(userKey, updatedUser);
    setUser(updatedUser);
  };

  // Check role
  const hasRole = (role) => {
    if (!user) return false;
    return user.role === role;
  };

  // Check permission
  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission) || user.permissions.includes('all');
  };

  const value = {
    isAuthenticated,
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    hasRole,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
